import { 
  AggregationType,
  BenchmarkType,
  FilterOperator, 
  filterOperatorFromJSON, 
  filterOperatorToJSON, 
  MetricEdgeType
} from "build-ts/enums";
import { AnalysisTreeNodeProto, AnalysisTreeProto, analysisTypeToJSON } from "build-ts/analysis";
import { MetricEdgeProto, MetricProto, Header, FilterProto } from "build-ts/metadata";
import { v4 as uuidv4 } from "uuid";
import { GrowthBenchmarkProto, GrowthBenchmarkType, DatePeriodEnum, DateFilterType } from "build-ts/relative_date";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";


// Function to get the analysis type
export const getAnalysisNodeName = (node: AnalysisTreeNodeProto, tree: AnalysisTreeProto) => {
  switch (analysisTypeToJSON(node?.analysis_type!)) {
    case "METRIC_VALUE_ANALYSIS":
      return `Compute ${tree?.metric?.header?.name}`;
    case "BENCHMARK_ANALYSIS":
      return `Compare to ${node?.benchmark?.header?.name}`;
    case "DRIVER_ANALYSIS":
      return `Split by ${node.driver_header?.name}`;
    case "CORRELATION_ANALYSIS":
      return `Split by ${node.fundamental_correlation_header?.name}`;
    default:
      return "Unknown Analysis Type";
  }
};

// Function to get the operator symbol
export const getOperatorSymbol = (operator: FilterOperator | string): string => {
  // Convert string to OperatorType enum if necessary
  const operatorEnum =
    typeof operator === "string" ? filterOperatorFromJSON(operator) : operator;

  // Define the mapping from OperatorType to symbols
  const operatorMap: Record<FilterOperator, string> = {
    [FilterOperator.EQ]: "=",
    [FilterOperator.GTE]: ">=",
    [FilterOperator.LTE]: "<=",
    [FilterOperator.NO_FILTER_OP]: "",
    [FilterOperator.NE]: "!=",
    [FilterOperator.GT]: ">",
    [FilterOperator.LT]: "<",
    [FilterOperator.CONTAINS]: "contains",
    [FilterOperator.NOT_CONTAINS]: "not contains",
    [FilterOperator.UNRECOGNIZED]: filterOperatorToJSON(FilterOperator.UNRECOGNIZED),
  };
  return operatorMap[operatorEnum] || filterOperatorToJSON(operatorEnum);
};

// Function to get initials from the tree name
export const getInitials = (name: string) => {

  // Remove special characters such as "(", ")", and "-" from the name
  const sanitized = name.replace(/[()\-_]/g, "");
  const words = sanitized.split(" ");
  if (words.length >= 2) {
    if (words[1][0] === "|") {
      return `${words[0][0]}`;  
    }
    return `${words[0][0]} ${words[1][0]}`; 
  } else if (words.length === 1) {
    return words[0][0]; 
  }
  return ""; 
};

// Function to get sort by label
export const getSortByLabel = (value: string) => {
  switch (value) {
    case "LAST_MODIFIED":
      return "Last Modified";
    case "CREATED":
      return "Created At";
    case "NAME":
      return "Name";
    default:
      return "";
  }
};

// Function to generate the tree name
export const generateName= (elements: string[]): string => {

  // Handle cases where elements is undefined or not an array
  if (!elements || !Array.isArray(elements) || elements.length === 0) {
    return "";
  }
  const lookupArray = ["Americas", "RoW", "Andean", "Brazil", "Turkey"];
  
  // 1. Take the metric as is
  const metric = elements[0];

  // 2. Process the time element
  const timeParts = elements[1].split(" ");
  const year = timeParts[timeParts.length - 1].slice(-2);
  let time = "";
  
  if (timeParts.length === 3) {
      time = `${timeParts[0][0]}${timeParts[1][0]}${year}`;
  } else if (timeParts.length === 2) {
      time = `${timeParts[0].slice(0, 3)}${year}`;
  }
  else if (timeParts.length === 1){
    time = timeParts[0];
  }

  // 3. Process the third element
  const items = elements[2].split(",").map(item => item.trim()).filter(item => item !== "");
  const processedItems = items.map(item => {
      if (lookupArray.includes(item)) {
          return item;
      }
      const itemParts = item.split(" ");
      return itemParts.map(part => part[0]).join("");
  });

  // Combine all parts
  const result = `${metric} | ${processedItems.join("/")} | ${time}`;
  return result;
};

/**
 * Maps action type strings to their corresponding display names.
 * @param actionType - The action type string to map.
 * @returns The display name for the action type.
 */
export const getActionTypeDisplayName = (actionType: string | undefined): string => {
  switch (actionType) {
    case "ASSIGN_TO_PAGE":
      return "Assign to Page";
    case "NOTIFY_SLACK":
      return "Notify Slack";
    case "GENERATE_COMMENTARY":
      return "Generate Commentary";
    case "QNA":
      return "Q&A";
    // Add more cases as needed for additional action types
    default:
      // Handle unknown or undefined action types
      return actionType
        ? actionType.split("_")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ")
        : "Action";
  }
};

/**
 * Determines the highest DatePeriod from date filters.
 * Returns the highest period found in the date filters, or null if no date filters exist.
 */
export const getHighestDatePeriod = (dateFilters: FilterProto[]): DatePeriodEnum | null => {
  if (!dateFilters || dateFilters.length === 0) {
    return null;
  }

  let highestPeriod: DatePeriodEnum | null = null;

  dateFilters.forEach(filter => {
    // Check if this filter has a date_filter field (FilterProto structure)
    if (filter?.date_filter?.date_period !== undefined) {
      const period = filter?.date_filter?.date_period;
      if (highestPeriod === null || period > highestPeriod) {
        highestPeriod = period;
      }
    }
    // Also check for different DateFilter types that might have different period fields
    else if (filter?.date_filter?.type !== undefined) {
      const dateFilter = filter?.date_filter;
      let period: DatePeriodEnum | null = null;
      
      // Check different DateFilter types
      if (dateFilter.type === DateFilterType.LAST_N_PERIOD || 
          dateFilter.type === DateFilterType.NEXT_N_PERIOD ||
          dateFilter.type === DateFilterType.THIS_PERIOD ||
          dateFilter.type === DateFilterType.PERIOD_TO_DATE) {
        period = dateFilter?.date_period!;
      }
      // For QUARTER_YEAR, assign QUARTER period
      else if (dateFilter.type === DateFilterType.QUARTER_YEAR) {
        period = DatePeriodEnum.QUARTER;
      }
      // For MONTH_YEAR, assign MONTH period
      else if (dateFilter.type === DateFilterType.MONTH_YEAR) {
        period = DatePeriodEnum.MONTH;
      }
      // For YEAR_ONLY, assign YEAR period
      else if (dateFilter.type === DateFilterType.YEAR_ONLY) {
        period = DatePeriodEnum.YEAR;
      }
      // For EXACT_DATE or EXACT_DATE_RANGE, assign DAY period
      else if (dateFilter.type === DateFilterType.EXACT_DATE || 
               dateFilter.type === DateFilterType.EXACT_DATE_RANGE) {
        period = DatePeriodEnum.DAY;
      }
      
      if (period !== null && (highestPeriod === null || period > highestPeriod)) {
        highestPeriod = period;
      }
    }
  });

  return highestPeriod;
};

/**
 * @returns KG Growth Benchmarks dynamically based on date filters.
 */
export const getKGGrowthBenchmarks = (dateFilters?: FilterProto[]): BenchmarkEntityProto[] => {
  const benchmarks: BenchmarkEntityProto[] = [];
  
  // Get the highest date period from filters
  const highestPeriod = getHighestDatePeriod(dateFilters || []);
  
  // If no date filters, return empty array (growth benchmarks only available with date filters)
  if (highestPeriod === null) {
    return benchmarks;
  }

  // Define all periods and their display names
  const allPeriods = [
    { period: DatePeriodEnum.DAY, name: "Day" },
    { period: DatePeriodEnum.WEEK, name: "Week" },
    { period: DatePeriodEnum.MONTH, name: "Month" },
    { period: DatePeriodEnum.QUARTER, name: "Quarter" },
    { period: DatePeriodEnum.YEAR, name: "Year" }
  ];

  // Create benchmarks for all periods >= highest period
  allPeriods.forEach(({ period, name }) => {
    if (period >= highestPeriod) {
      // Create LAST_X benchmark
      const lastBenchmark = BenchmarkEntityProto.create({
        header: {
          name: `Last ${name}`,
          id: uuidv4(),
          benchmark_type: BenchmarkType.VERSUS_BENCHMARK,
        },
        benchmark_type: BenchmarkType.VERSUS_BENCHMARK,
        definition_mapping: [],
        versus_filters: [],
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: 1,
          growth_period: period,
        }),
      });

      // Create LAST_N_X_AVERAGE benchmark
      const lastNBenchmark = BenchmarkEntityProto.create({
        header: {
          name: `Last N ${name} Average`,
          id: uuidv4(),
          benchmark_type: BenchmarkType.VERSUS_BENCHMARK,
        },
        benchmark_type: BenchmarkType.VERSUS_BENCHMARK,
        definition_mapping: [],
        versus_filters: [],
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: 4, // Default to 4, will be overridden by user selection
          aggregation_type: AggregationType.AVERAGE,
          growth_period: period,
        }),
      });

      // Create N_PERIOD_AGO benchmark (without aggregation)
      const nPeriodAgoBenchmark = BenchmarkEntityProto.create({
        header: {
          name: `N ${name} Ago`,
          id: uuidv4(),
          benchmark_type: BenchmarkType.VERSUS_BENCHMARK,
        },
        benchmark_type: BenchmarkType.VERSUS_BENCHMARK,
        definition_mapping: [],
        versus_filters: [],
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: 1, // Default to 1, will be overridden by user selection
          growth_period: period,
          // No aggregation_type - this distinguishes it from the average benchmark
        }),
      });

      benchmarks.push(lastBenchmark, lastNBenchmark, nPeriodAgoBenchmark);
    }
  });

  return benchmarks;
};

/**
 * @returns KG Benchmark Entities from Metric Edges.
 */
export const getBenchmarkEntityFromEdges = (edges: MetricEdgeProto[]) : BenchmarkEntityProto[] => {
  const benchmarkEdges = edges.filter((edge) => 
    edge.edge_type === MetricEdgeType.METRIC_BENCHMARK_EDGE);
  return benchmarkEdges.map((edge) => {
    return BenchmarkEntityProto.create({
      header: edge.header
    });
  });
};

/**
 * Processes multiple metrics and returns combined edges based on edge type:
 * - Attribute edges: Intersection of all metrics' attribute edges
 * - Measure edges: Union of all metrics' measure edges
 * - Benchmark edges: Intersection of all metrics' benchmark edges
 * - Temporal edges: Union of all metrics' temporal edges
 * 
 * @param metrics Array of metric protos to process
 * @returns Object containing processed edges by type
 */
export const mergeMetricEdgesByType = (
  metrics: MetricProto[]
): {
  attributeHeaders: Header[];
  measureHeaders: Header[];
  metricEdgeHeaders: Header[];
  benchmarkEntities: BenchmarkEntityProto[];
  temporalHeaders: Header[];
  metricsHeaders: Header[];
} => {
  if (!metrics || metrics.length === 0) {
    return {
      attributeHeaders: [],
      measureHeaders: [],
      metricEdgeHeaders: [],
      benchmarkEntities: [],
      temporalHeaders: [],
      metricsHeaders: [],
    };
  }

  // Helper for the single-metric case
  const extractSingle = (m: MetricProto) => {
    const edges = m.edges || [];
    return {
      attributeHeaders: edges
        .filter(e => e.edge_type === MetricEdgeType.METRIC_ATTRIBUTE_EDGE)
        .map(e => e.header!)
        .filter(Boolean),
      measureHeaders: edges
        .filter(e => e.edge_type === MetricEdgeType.METRIC_MEASURE_EDGE)
        .map(e => e.header!)
        .filter(Boolean),
      metricEdgeHeaders: edges
        .filter(e => e.edge_type === MetricEdgeType.METRIC_METRIC_EDGE)
        .map(e => e.header!)
        .filter(Boolean),
      temporalHeaders: edges
        .filter(e => e.edge_type === MetricEdgeType.METRIC_TIMEFRAME_EDGE)
        .map(e => e.header!)
        .filter(Boolean),
      benchmarkEntities: getBenchmarkEntityFromEdges(edges).filter(Boolean),
      metricsHeaders: [m?.header!],
    };
  };

  if (metrics.length === 1) {
    return extractSingle(metrics[0]);
  }
  // 1) ATTRIBUTE (INTERSECTION)
  const attrMap = new Map<string, Header>();
  extractSingle(metrics[0]).attributeHeaders.forEach(h => attrMap.set(h?.id || "", h));
  for (let i = 1; i < metrics.length; i++) {
    const ids = new Set(
      extractSingle(metrics[i]).attributeHeaders.map(h => h.id)
    );
    for (const id of Array.from(attrMap.keys())) {
      if (!ids.has(id)) attrMap.delete(id);
    }
  }
  // 2) BENCHMARKS (INTERSECTION)
  const benchMap = new Map<string, BenchmarkEntityProto>();
  extractSingle(metrics[0]).benchmarkEntities.forEach(b =>
    benchMap.set(b.header?.id || "", b)
  );
  for (let i = 1; i < metrics.length; i++) {
    const ids = new Set(
      extractSingle(metrics[i]).benchmarkEntities.map(b => b.header?.id || "")
    );
    for (const id of Array.from(benchMap.keys())) {
      if (!ids.has(id)) benchMap.delete(id);
    }
  }
  // 3) TEMPORAL (INTERSECTION)
  const tempMap = new Map<string, Header>();
  extractSingle(metrics[0]).temporalHeaders.forEach(h =>
    tempMap.set(h?.id || "", h)
  );
  for (let i = 1; i < metrics.length; i++) {
    const ids = new Set(
      extractSingle(metrics[i]).temporalHeaders.map(h => h?.id || "")
    );
    for (const id of Array.from(tempMap.keys())) {
      if (!ids.has(id)) tempMap.delete(id);
    }
  }
  // 4) MEASURE (UNION)
  const measureMap = new Map<string, Header>();
  metrics.forEach(m =>
    extractSingle(m).measureHeaders.forEach(h =>
      measureMap.set(h?.id || "", h)
    )
  );
  // 5) METRIC‐METRIC (UNION)
  const metricEdgeMap = new Map<string, Header>();
  metrics.forEach(m =>
    extractSingle(m).metricEdgeHeaders.forEach(h =>
      metricEdgeMap.set(h?.id || "", h)
    )
  );

  return {
    attributeHeaders: Array.from(attrMap.values()),
    measureHeaders: Array.from(measureMap.values()),
    metricEdgeHeaders: Array.from(metricEdgeMap.values()),
    benchmarkEntities: Array.from(benchMap.values()),
    temporalHeaders: Array.from(tempMap.values()),
    metricsHeaders: metrics?.map(m => m?.header!),
  };
};

